import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import Loading from '../../../components/loading';
import EventModel from '../../../models/eventModel';

import { ToastContext, WeezHeader, Button, Card, Message, AnchorNav } from '@weezevent/nacre';
import { useForm, requiredField, websiteField } from '../../../utils/validators';
import DateForm from './dateForm';
import DescriptionForm from './descriptionForm';
import useAppContext from '../../../context/useAppContext';
import TaxModel from '../../../models/taxModel';
import EventCreationLocalization from '../../event_creation/eventCreationLocalization';
import SubtypeEventModel from '../../../models/subtypeEventModel';
import ContactForm from "./contactForm";
import Inventory  from '../../../models/inventory';
import { LANGUAGES } from '../../../components/translations/translationModal';
import moment from 'moment-timezone';


function isAutoModeAvailable(formAddress) {
    let enabled = true;
    if (!formAddress) {
        return enabled;
    }
    Object.entries(formAddress).forEach(([key, value]) => {
        if (!['address2', 'coordinates'].includes(key)) {
            enabled = enabled && (!value || value === '')
        }
    });

    return enabled;
}

export function websiteFieldOnlineEvent(name, value, form) {
    if(form.localization_type === 'online'){
        if (!value)
            return 'ticket.common.required';
    }
    return true;
}
export default function EventForm({ menhirUrl }) {
    const [{ organization, event: evt, reloadEvent: reloadEventContext }] = useAppContext();
    const { t } = useTranslation();
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [autoMode, setAutoMode] = React.useState(true);
    const [isActiveAutoMode, setIsActiveAutoMode] = React.useState(true);

    const toast = React.useContext(ToastContext);
    const [eventSubtypes, loadingSubtypes] = SubtypeEventModel.useApiModel({
        organizationId: organization.id
    });
    const [taxes] = TaxModel.useApiModel({});

    function contactRequiredFields(name, value, form) {
        const obj = {};
        const required = ['email'];
        for (const field of required) {
            // eslint-disable-next-line no-irregular-whitespace
            if (!(field in value) || value[field] === "" || value[field] === null) {
                obj[field] = 'ticket.common.required'
            }
        }
        if (value['website']) {
            obj['website'] = websiteField(name, value['website'], form)
        }
        Object.entries(obj).forEach(([key, value]) => {
            if (value === true) {
                delete obj[key]
            }
        })
        return Object.keys(obj).length ? obj : true;
    }

    const inventoriesValidation = React.useCallback((name, inventories, form) => {
        if (form.use_sessions || !inventories.length) {
            return true;
        }
        if (inventories.map(inv => inv.start_date).some((e, i, arr) => e && arr.indexOf(e) !== i)) {
            return 'ticket.creationEvent.dates.error-double';
        }
        if ((inventories.some(inv => inv.start_date && (inv.start_date < form.start_date))) && (inventories.some(inv => inv.start_date && (form.end_date <= inv.start_date)))) {
            return 'ticket.creationEvent.dates.error-start-and-end-date';
        }
        if (inventories.some(inv => inv.start_date && (inv.start_date < form.start_date))) {
            return 'ticket.creationEvent.dates.error-before-start-date';
        }
        if (form.end_date && inventories.some(inv => inv.start_date && (form.end_date <= inv.start_date))) {
            return 'ticket.creationEvent.dates.error-after-end-date';
        }
        return true;
    }, []);


    const validateStartDate = React.useCallback((name, date, formEvent) => {
        if (!formEvent.start_date) {
            return 'ticket.event.no_date_start';
        }
        return true;
    }, []);

    const validateDate = React.useCallback((name, date, formEvent) => {
        if (!formEvent.start_date) {
            return;
        }
        let startTzEvent = moment.tz(formEvent.start_date, formEvent.timezone);

        if (formEvent.end_date) {
            let endTzEvent = moment.tz(formEvent.end_date, formEvent.timezone);
            if (startTzEvent.isAfter(endTzEvent)) {
                return 'ticket.event.dateend_before_error';
            }
        }
        return true;
    }, []);

    const defaultContact = React.useMemo(() => {
        return {
            address: organization.address,
            blog: "",
            email: organization.email,
            entity: organization.name,
            first_name: organization.first_name,
            function: "",
            last_name: organization.last_name,
            licence: organization.license_number,
            mobile_number: organization.mobile_phone_number,
            phone_number: organization.phone_number,
            use_contact: false,
            website: organization.website
        }
    }, [organization]);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            type: requiredField,
            tax_id: requiredField,
            contact_data: contactRequiredFields,
            website: websiteFieldOnlineEvent,
            inventories: inventoriesValidation,
            start_date: validateStartDate,
            end_date: validateDate
        };
    }, [contactRequiredFields, websiteFieldOnlineEvent, inventoriesValidation, validateStartDate, validateDate]);

    const [formEvent, setFormEvent, setFormEventObject, eventErrors, eventValidate] = useForm({
        name: "",
        subtitle: "",
        surtitle: "",
        timezone: null,
        type: null,
        website: "",
        localization_type: 'location',
        default_language: organization.default_locale.replace('_', '-').toLowerCase(),
        address: {
            name: "",
            address1: "",
            address2: "",
            city: "",
            country: "",
            state: "",
            zip_code: ""
        },
        description: "",
        subtype: null,
        end_date: "",
        start_date: "",
        full_picture: null,
        full_address: null,
        use_sessions: false,
        tax_id: 0,
        contact_data: {},
        inventories: [],
        translations: {
            "description": {}
        },
    }, validators);

    const onChangeForm = React.useCallback(
        (fieldName, value) => {
            const defaultOptions = { 
                disableValidation: true, 
                cleanErrors: true 
            };

            setFormEvent(fieldName, value, defaultOptions);
        },
        [setFormEvent]
    );

    const onChangeFormObject = React.useCallback(
        (obj, merge = false) => {
            const defaultOptions = {
                cleanErrors: true 
            };

            setFormEventObject(obj, merge, defaultOptions);
        },
        [setFormEventObject]
    );

    const [event, loadingEvent, syncEvent] = EventModel.useApiModel({
        id: evt.pk,
        organizationId: organization.id,
        query: { sync: "True", expand: "contact" },
        onSuccess: (event) => {
            //@Todo if a supprimer des que full_address sera aussi renvoyé par le back lors d'un get
            event.full_address = null;

            event.localization_type = "location";

            if (['online_donations', 'memberships'].includes(event.subtype) && !event.address.address1.length && !event.website) {
                event.localization_type = "no_location";
            }

            if (!event.address.address1.length && event.website) {
                event.localization_type = "online";
            }

            if (!event.tax_id) {
                // default event tax id if no tax_id
                event.tax_id = 0;
            }

            event.contact_data = defaultContact;
            //if use_contact === true, we load custom contact data
            if (event.contact && event.contact.use_contact) {
                event.contact_data = { ...event.contact_data, ...event.contact };
                if (!event.contact_data.entity) {
                    event.contact_data.entity = organization.name
                }
            }
            onChangeFormObject({ ...event.object }, false);

            //Check if we activate auto complete google address
            let _isAutoModeAvailable = isAutoModeAvailable(event.address);
            setIsActiveAutoMode(_isAutoModeAvailable);
            setAutoMode(_isAutoModeAvailable);
        }
    }, [isAutoModeAvailable, organization, defaultContact]);

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : organization.default_locale.replace('_', '-').toLowerCase();
        return defaultLocale;
    }, [organization, languageOptions, event]);

    const [inventories, loadingInventories] = Inventory.useApiModel(
        {
            organizationId: organization.pk,
            eventId: evt.id,
            query: {
                only_event_date_inventories: true
            },
            launch: Boolean(!loadingEvent && evt.isFakeSeating()),
            onSuccess: _inventories => {
                if (_inventories.length) {
                    const smallestDate = _inventories.map(e => e.start_date).sort()[0];
                    // using object deep copy to keep only the data and to prevent copying each proxy with its ref
                    const unrefInventories = _inventories.map(i => {return {...i.object}});
                    onChangeFormObject({'start_date': smallestDate, 'inventories': [...unrefInventories]}, true);
                }
            }
        },
        [organization, evt, loadingEvent]
    );

    const handleSubmit = React.useCallback(() => {
        const _inventories = formEvent.inventories;
        setSubmitting(true);
        let formValid = eventValidate();
        if (formValid) {
            let _form = {...formEvent}
            // We don't wan't to pass the chart in case the task to transform the event in
            // fakeseating is still executing in the background
            delete _form['chart'];
            delete _form['inventories'];
            let eventObject = new EventModel(_form);

            if (eventObject.tax_id === 0) {
                eventObject.tax_id = null;
            }

            eventObject
                .save({ organizationId: organization.id, query: { expand: 'contact' } })
                .then(() => {
                    if (evt.isFakeSeating() && !eventObject.use_sessions) {
                        let inventoryPromises = [];
                        _inventories.filter(i => i.start_date).forEach(inventory => {
                            if (!inventory.uuid) {
                                inventoryPromises.push(
                                    new Inventory({
                                        name: inventory.start_date,
                                        start_date: inventory.start_date,
                                        event: evt.id,
                                        chart: evt.chart
                                    }).save({organizationId: organization.id, eventId: evt.id})
                                )
                            } else {
                                const updatedInventory = inventories.find(inv => inv.uuid === inventory.uuid)
                                if (updatedInventory) {
                                    if (updatedInventory.start_date && (updatedInventory.start_date !== inventory.start_date)) {
                                        updatedInventory.start_date = inventory.start_date;
                                        updatedInventory.name = inventory.start_date;
                                        inventoryPromises.push(updatedInventory.save({organizationId: organization.id, eventId: evt.id}))
                                    }
                                } else {
                                    console.warn(`Inventory with uuid ${inventory.uuid} not found among loaded inventories`);
                                }
                            }
                        })
                        Promise.all(inventoryPromises)
                            .then(() => {
                                syncEvent();
                                setSubmitting(false);
                                reloadEventContext(false);
                                toast.success(t(`ticket.event.editSuccess`));
                            })
                            .catch((e) => {
                                setSubmitting(false);
                                reloadEventContext(false);
                                toast.error(t(`ticket.event.editError`));
                            })
                    } else {
                        syncEvent();
                        setSubmitting(false);
                        reloadEventContext(false);
                        toast.success(t(`ticket.event.editSuccess`));
                    }
                })
                .catch(e => {
                    setSubmitting(false);
                    reloadEventContext(false);
                    toast.error(t(`ticket.event.editError`));
                })
        } else {
            setSubmitting(false);
        }
    }, [setSubmitting, organization, inventories, eventValidate, evt, formEvent, syncEvent, reloadEventContext]);

    if (loadingEvent || loadingSubtypes || (loadingInventories && evt.isFakeSeating())) {
        return <Loading />;
    }

    return (
        <div>
            <WeezHeader
                title={t('ticket.event.event_data.title')}
                rightComponent={[
                    <Button primary
                        key={0}
                        label={t('ticket.common.action.save')}
                        onClick={handleSubmit}
                        isLoadding={isSubmitting}
                    />
                ]}
            >
                {t('ticket.event.event_data.subtitle')}

            </WeezHeader>
            <AnchorNav
                rootId='main'
                items={[
                    {
                        id: 'event-form-description',
                        title: t('ticket.event.description')
                    },
                    {
                        id: 'event-form-location',
                        title: t('ticket.event.location')
                    },
                    {
                        id: 'event-form-dates',
                        title: t('ticket.event.edit.datesTitle')
                    },
                    {
                        id: 'event-form-contact',
                        title: t('ticket.event.edit.contactTitle')
                    }
                ]}
            >
                <DescriptionForm
                    event={event}
                    formEvent={formEvent}
                    onChange={onChangeForm}
                    taxes={taxes}
                    onChangeObject={onChangeFormObject}
                    organization={organization}
                    isSubmitting={isSubmitting}
                    eventSubtypes={eventSubtypes}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                    errors={eventErrors}
                />
                <Card.FormStep
                    title={t('ticket.event.location')}
                    subtitle={t('ticket.event.edit.localizationSubtitle')}
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            {t('ticket.event.edit.localizationTips')}
                        </Message>
                    }
                    id='event-form-location'
                >
                    <EventCreationLocalization
                        formEvent={formEvent}
                        onChange={onChangeForm}
                        isSubmitting={isSubmitting}
                        onChangeObject={address => onChangeFormObject(address, true)}
                        autoMode={autoMode}
                        setAutoMode={setAutoMode}
                        disabledAutoMode={!isActiveAutoMode}
                        toggleFakeseating={true}
                        errors={eventErrors}
                        edit={true}
                    />
                </Card.FormStep>
                <DateForm
                    eventUseSessions={Boolean(event?.use_sessions)}
                    formEvent={formEvent}
                    onChange={onChangeForm}
                    eventErrors={eventErrors}
                    onChangeObject={onChangeFormObject}
                    isSubmitting={isSubmitting} />
                <ContactForm formContact={formEvent.contact_data} defaultFormContact={defaultContact} onChange={onChangeForm} eventErrors={eventErrors} />
            </AnchorNav>
        </div>
    );
}

EventForm.defaultProps = {
    event: null
};

EventForm.propTypes = {
    menhirUrl: PropTypes.string.isRequired
};
